import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const HOVER_INTENT_DELAY = 100;
const CLOSE_GRACE_DELAY = 200;

/**
 * Normalize a submenu activation delay to a safe integer.
 * @param {string | null | undefined} value
 * @returns {number}
 */
function parseDelay(value, fallback) {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  #injectScheduled = false;
  #injectMutationObserver = new MutationObserver(() => {
    if (this.#injectScheduled) return;
    this.#injectScheduled = true;
    requestAnimationFrame(() => {
      this.#injectScheduled = false;
      this.#injectCustomMegaMenuImages();
    });
  });

  get activateDelayMs() {
    return parseDelay(this.dataset.activateDelay, HOVER_INTENT_DELAY);
  }

  get deactivateDelayMs() {
    return parseDelay(this.dataset.deactivateDelay, CLOSE_GRACE_DELAY);
  }

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
      if (this.#state.activeItem) this.#debouncedDeactivate();
    }, { signal: this.#abortController.signal });

    this.addEventListener('keydown', (event) => this.#handleMenuKeydown(event), {
      signal: this.#abortController.signal,
    });

    this.querySelectorAll('[ref="submenu[]"]').forEach((submenu) => {
      if (!(submenu instanceof HTMLElement)) return;
      submenu.setAttribute('inert', '');
      submenu.addEventListener('pointerenter', () => this.#debouncedDeactivate.cancel(), {
        signal: this.#abortController.signal,
      });
      submenu.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
        signal: this.#abortController.signal,
      });
    });

    this.#injectCustomMegaMenuImages();
    onDocumentLoaded(() => {
      this.#injectCustomMegaMenuImages();
    });

    this.#injectMutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['slot'],
    });

    this.#syncGlobalMenuState(this.ariaExpanded === 'true');
    this.#syncTopLevelLinkOpacity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#injectMutationObserver.disconnect();
    this.#syncGlobalMenuState(false);
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    visualItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    // Only treat the actual overflow panel as "hovered" so we can still
    // close mega menus when moving between top-level items.
    return this.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item) return;
    if (item !== this.#state.visualItem) {
      this.#setVisualItem(item);
    }
    if (item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    const previouslyActiveItem = this.#state.activeItem;

    let submenu = findSubmenu(item);
    if ((!submenu || hasDisplayNone(submenu)) && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // If the hovered item has no submenu, make sure the background doesn't "flash"
    // and close any previously opened mega menu.
    if (!(submenu instanceof HTMLElement) || hasDisplayNone(submenu)) {
      this.#debouncedDeactivate.cancel();
      if (previouslyActiveItem) this.#closeActiveSubmenu();
      return;
    }

    submenu.removeAttribute('inert');

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      findSubmenu(previouslyActiveItem)?.setAttribute('inert', '');
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    this.style.setProperty('--submenu-opacity', '1');
    this.#syncGlobalMenuState(true);
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, HOVER_INTENT_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Clear either the open submenu item or a visual-only hovered item.
    if (item === this.#state.activeItem || item === this.#state.visualItem) {
      this.#debouncedDeactivate(item);
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options]
   */
  #deactivate = (item = this.#state.activeItem, options = {}) => {
    if (!item) return;
    if (item !== this.#state.activeItem && item !== this.#state.visualItem) return;
    const activeSubmenu = this.#state.activeItem ? findSubmenu(this.#state.activeItem) : null;
    const submenuHovered = activeSubmenu?.matches(':hover') ?? false;
    const submenuFocused = activeSubmenu?.contains(document.activeElement) ?? false;
    if (!options.force && (this.overflowHovered || submenuHovered || submenuFocused)) return;

    this.#closeActiveSubmenu();
    this.#clearVisualItem();
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, CLOSE_GRACE_DELAY);

  /**
   * Keep one visual active item and dim its siblings, matching the Maha menu treatment.
   * @param {HTMLElement} item
   */
  #setVisualItem = (item) => {
    const currentListItem = item.closest('.menu-list__list-item');
    if (!(currentListItem instanceof HTMLElement)) return;

    this.#state.visualItem = item;

    this.querySelectorAll('.menu-list__list-item').forEach((listItem) => {
      if (!(listItem instanceof HTMLElement)) return;
      if (listItem.getAttribute('slot')) return;
      listItem.classList.remove('active', 'non-active');
      listItem.classList.add(listItem === currentListItem ? 'active' : 'non-active');
    });

    this.#syncTopLevelLinkOpacity();
  };

  /**
   * Clear visual dimming classes from top-level menu items.
   */
  #clearVisualItem = () => {
    this.#state.visualItem = null;
    this.querySelectorAll('.menu-list__list-item').forEach((listItem) => {
      if (!(listItem instanceof HTMLElement)) return;
      listItem.classList.remove('active', 'non-active');
    });

    this.#syncTopLevelLinkOpacity();
  };

  /**
   * The dimmed / active state of top-level links is driven by the `active` and `non-active`
   * classes set above; the matching opacities live in custom-header.css.
   */
  #syncTopLevelLinkOpacity = () => {
    this.#syncTopLevelLinkAria();
  };

  /**
   * Ensure top-level menu links always expose the correct aria-expanded state.
   */
  #syncTopLevelLinkAria = () => {
    this.querySelectorAll('.menu-list__list-item').forEach((listItem) => {
      if (!(listItem instanceof HTMLElement)) return;
      if (listItem.getAttribute('slot')) return;

      const link = listItem.querySelector(':scope > .menu-list__link');
      if (!(link instanceof HTMLElement)) return;

      link.setAttribute('aria-expanded', listItem === this.#state.activeItem?.closest('.menu-list__list-item') ? 'true' : 'false');
    });
  };

  /**
   * Sync global menu-open state for overlay and header styling without expensive selector chains.
   * @param {boolean} isOpen
   */
  #syncGlobalMenuState = (isOpen) => {
    document.body.classList.toggle('mega-open', isOpen);

    const headerComponent = this.closest('header-component');
    if (headerComponent instanceof HTMLElement) {
      headerComponent.dataset.megaOpen = isOpen ? 'true' : 'false';
    }

    const headerSection = this.closest('.header-section');
    if (headerSection instanceof HTMLElement) {
      headerSection.dataset.megaOpen = isOpen ? 'true' : 'false';
    }
  };

  /**
   * Close the currently open submenu shell state.
   */
  #closeActiveSubmenu = (options = {}) => {
    const currentItem = this.#state.activeItem;
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    if (currentItem instanceof HTMLElement) {
      currentItem.ariaExpanded = 'false';
      currentItem.closest('.menu-list__list-item')?.querySelector('.menu-list__submenu')?.setAttribute('inert', '');
      currentItem.removeAttribute('data-focus-origin');
    }
    this.#syncGlobalMenuState(false);
  };

  /**
   * Handle keyboard interactions for the menu.
   * @param {KeyboardEvent} event
   */
  #handleMenuKeydown = (event) => {
    if (event.key === 'Escape') {
      if (!this.#state.activeItem) return;
      const activeItem = this.#state.activeItem;
      this.#closeActiveSubmenu();
      const link = activeItem.closest('.menu-list__list-item')?.querySelector('.menu-list__link');
      if (link instanceof HTMLElement) link.focus();
      event.preventDefault();
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      const link = event.target.closest('.menu-list__link');
      if (!(link instanceof HTMLElement)) return;
      if (!link.hasAttribute('aria-haspopup')) return;
      event.preventDefault();
      const submenu = findSubmenu(link.closest('.menu-list__list-item'));
      if (!submenu) return;

      const item = findMenuItem(link);
      if (!item) return;

      const isExpanded = this.#state.activeItem === item;
      if (isExpanded) {
        this.#closeActiveSubmenu();
      } else {
        this.#activateHandler({ target: link });
      }
    }
  };

  /**
   * Inject custom mega-menu image cards from hidden template blocks.
   * This is a runtime fallback for contexts where block settings are not directly exposed in Liquid.
   */
  #injectCustomMegaMenuImages = () => {
    const templates = Array.from(
      this.querySelectorAll('template[data-mega-menu-custom-image][data-menu-handle]')
    );

    if (!templates.length) return;

    const defaultLayout = normalizeCustomMediaLayout(this.dataset.customMediaLayoutDefault, 'stack');
    const defaultColumnsDesktop = parsePositiveInt(this.dataset.customMediaColumnsDefault, 2);
    const defaultWidth = normalizeCustomMediaWidth(this.dataset.customMediaWidthDefault, 'medium');
    const defaultTextColumns = Math.min(5, parsePositiveInt(this.dataset.textColumnsDefault, 4));

    /** @type {Map<string, { layout: string, width: string, columnsDesktop: number, textColumns: number }>} */
    const configByHandle = new Map();
    this.querySelectorAll('[data-mega-menu-trigger-config][data-menu-handle]').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      const menuHandle = normalizeHandle(element.dataset.menuHandle);
      if (!menuHandle) return;

      configByHandle.set(menuHandle, {
        layout: normalizeCustomMediaLayout(element.dataset.mediaLayout, defaultLayout),
        width: normalizeCustomMediaWidth(element.dataset.mediaWidth, defaultWidth),
        columnsDesktop: Math.min(2, parsePositiveInt(element.dataset.mediaColumnsDesktop, defaultColumnsDesktop)),
        textColumns: Math.min(5, parsePositiveInt(element.dataset.textColumnsDesktop, defaultTextColumns)),
      });
    });

    /** @type {Map<string, HTMLTemplateElement[]>} */
    const templatesByHandle = new Map();

    templates.forEach((template) => {
      if (!(template instanceof HTMLTemplateElement)) return;
      const menuHandle = normalizeHandle(template.dataset.menuHandle);
      if (!menuHandle) return;

      const list = templatesByHandle.get(menuHandle) ?? [];
      list.push(template);
      templatesByHandle.set(menuHandle, list);
    });

    const menuLinks = Array.from(this.querySelectorAll('[ref="menuitem"][aria-controls]'));

    menuLinks.forEach((menuLink) => {
      if (!(menuLink instanceof HTMLAnchorElement)) return;

      const submenu = findSubmenu(menuLink);
      if (!(submenu instanceof HTMLElement)) return;

      const grid = submenu.querySelector('.mega-menu__grid');
      if (!(grid instanceof HTMLElement)) return;

      const titleHandle = normalizeHandle(menuLink.textContent);
      const hrefHandle = normalizeHandle(extractHandleFromHref(menuLink.getAttribute('href')));
      const matchedTemplates = templatesByHandle.get(titleHandle) ?? templatesByHandle.get(hrefHandle);
      const matchedConfig = configByHandle.get(titleHandle) ?? configByHandle.get(hrefHandle);
      const textColumns = matchedConfig?.textColumns ?? defaultTextColumns;
      const availableMediaColumns = Math.max(1, 6 - textColumns);
      const columnsDesktop = Math.min(
        2,
        availableMediaColumns,
        matchedConfig?.columnsDesktop ?? defaultColumnsDesktop
      );

      grid.style.setProperty('--mega-menu-text-columns', String(textColumns));
      grid.style.setProperty('--mega-menu-media-columns', String(columnsDesktop));

      const existingImageLists = Array.from(grid.querySelectorAll('.mega-menu__content-list--images'));
      if (existingImageLists.length) {
        grid.dataset.hasCustomMedia = 'true';
        const hasServerRenderedImages = existingImageLists.some(
          (imageList) => imageList.dataset.injectedCustom !== 'true'
        );

        // If section re-rendering added server-rendered media after a runtime injection,
        // remove stale injected lists to avoid duplicate custom cards.
        if (hasServerRenderedImages) {
          existingImageLists
            .filter((imageList) => imageList.dataset.injectedCustom === 'true')
            .forEach((imageList) => imageList.closest('.mega-menu__content')?.remove());
        }

        return;
      }

      // Preserve featured product/collection layouts.
      if (grid.querySelector('.mega-menu__content-list--products, .mega-menu__content-list--collections')) return;

      if (!matchedTemplates?.length) return;
      const layout = matchedConfig?.layout ?? defaultLayout;
      const width = matchedConfig?.width ?? defaultWidth;

      const content = document.createElement('span');
      content.className = 'mega-menu__content';
      content.dataset.customMediaLayout = layout;
      content.dataset.customMediaWidth = width;
      content.style.setProperty('--menu-content-columns-desktop', String(columnsDesktop));
      content.style.setProperty('--menu-content-columns-tablet', layout === 'grid' ? '2' : '1');

      const list = document.createElement('ul');
      list.className = 'mega-menu__content-list mega-menu__content-list--images list-unstyled';
      list.dataset.injectedCustom = 'true';
      list.dataset.customMedia = 'true';
      list.dataset.mediaLayout = layout;

      // Publish the spans as custom properties and let the stylesheet pick the breakpoint, so the
      // layout follows a window resize instead of freezing at whatever width it was injected on.
      const textList = grid.querySelector('.mega-menu__list');
      if (textList instanceof HTMLElement) {
        textList.style.setProperty('--mega-menu-text-columns', String(textColumns));
        textList.style.setProperty('--mega-menu-text-columns-tablet', String(Math.max(1, 4 - (layout === 'grid' ? 2 : 1))));
      }

      matchedTemplates.forEach((template) => {
        const firstElement = template.content.firstElementChild;
        if (!(firstElement instanceof HTMLElement)) return;

        const clonedItem = /** @type {HTMLElement} */ (firstElement.cloneNode(true));
        if (template.dataset.linkEmpty === 'true') {
          const link = clonedItem.querySelector('.resource-card__link');
          if (link instanceof HTMLAnchorElement) {
            link.href = menuLink.href;
          }
        }

        list.append(clonedItem);
      });

      if (!list.childElementCount) return;
      if (layout === 'hero' && list.childElementCount > 1) {
        content.style.setProperty('--menu-content-columns-desktop', '2');
      }
      const imageSizes = getCustomMediaSizes(list.childElementCount);
      list.querySelectorAll('img').forEach((image) => {
        if (!(image instanceof HTMLImageElement)) return;
        image.sizes = imageSizes;
      });
      list.dataset.mediaCount = String(list.childElementCount);
      grid.dataset.hasCustomMedia = 'true';
      content.append(list);
      grid.append(content);
    });
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Prefer the first overflowing item that actually has a submenu.
    // Falling back to the first overflow item can open an empty panel when the
    // first item is a simple link without children.
    const overflowItems = Array.from(element.parentElement?.querySelectorAll('[slot="overflow"]') ?? []);
    const firstOverflowWithSubmenu = overflowItems.find((item) => item.querySelector('[ref="submenu[]"]'));

    return findMenuItem(firstOverflowWithSubmenu ?? overflowItems[0] ?? null);
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Checks if the element is explicitly hidden by display:none.
 * @param {Element | null | undefined} element
 * @returns {boolean}
 */
function hasDisplayNone(element) {
  if (!(element instanceof Element)) return true;
  return window.getComputedStyle(element).display === 'none';
}

/**
 * Convert a menu label/handle into a normalized key for matching.
 * @param {string | null | undefined} value
 * @returns {string}
 */
function normalizeHandle(value) {
  if (!value) return '';
  return value
    .toLowerCase()
    .trim()
    .replace(/-mega-menu/g, '')
    .replace(/-mega menu/g, '')
    .replace(/ mega-menu/g, '')
    .replace(/ mega menu/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Normalize a custom media layout value to a supported option.
 * @param {string | null | undefined} value
 * @param {string} [fallback]
 * @returns {'single' | 'stack' | 'grid' | 'hero'}
 */
function normalizeCustomMediaLayout(value, fallback = 'stack') {
  if (value === 'single' || value === 'stack' || value === 'grid' || value === 'hero') return value;
  if (fallback === 'single' || fallback === 'stack' || fallback === 'grid' || fallback === 'hero') return fallback;
  return 'stack';
}

/**
 * Normalize a custom media width value to a supported option.
 * @param {string | null | undefined} value
 * @param {string} [fallback]
 * @returns {'small' | 'medium' | 'large'}
 */
function normalizeCustomMediaWidth(value, fallback = 'medium') {
  if (value === 'small' || value === 'medium' || value === 'large') return value;
  if (fallback === 'small' || fallback === 'medium' || fallback === 'large') return fallback;
  return 'medium';
}

/**
 * Parse a positive integer from a string-like value with a fallback.
 * @param {string | null | undefined} value
 * @param {number} fallback
 * @returns {number}
 */
function parsePositiveInt(value, fallback) {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Mirror Extra Butter's custom media image request sizing.
 * Menus with two cards use a 5-column desktop rhythm, three cards use 6.
 * @param {number} mediaCount
 * @returns {string}
 */
function getCustomMediaSizes(mediaCount) {
  const desktopDivisor = mediaCount > 0 && mediaCount < 3 ? 5 : 6;
  return `(min-width: 1024px) calc(100vw / ${desktopDivisor}), calc(100vw / 2)`;
}

/**
 * Extract last path segment as a handle candidate.
 * @param {string | null | undefined} href
 * @returns {string}
 */
function extractHandleFromHref(href) {
  if (!href) return '';
  try {
    const url = new URL(href, window.location.origin);
    const segments = url.pathname.split('/').filter(Boolean);
    return segments.at(-1) ?? '';
  } catch {
    return '';
  }
}
